import React, { useState, useEffect } from 'react';

const Court = ({ 
  courtData, 
  onScoreSubmit, 
  allPlayers, 
  competitionLevel, 
  gameInfo = null,
  getTeamName = null
}) => {
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [timer, setTimer] = useState('0:00');
  
  // MLP-specific scoring states
  const [team1Won15, setTeam1Won15] = useState(false);
  const [team2Won15, setTeam2Won15] = useState(false);

  // Timer logic for SMART sessions only
  useEffect(() => {
    if (!courtData.isActive || gameInfo) return; // Skip timer for MLP

    const interval = setInterval(() => {
      if (courtData.timer.startTime) {
        const elapsed = Math.floor((Date.now() - courtData.timer.startTime) / 1000);
        const duration = courtData.timer.duration;
        
        if (elapsed <= duration) {
          const remaining = duration - elapsed;
          const minutes = Math.floor(remaining / 60);
          const seconds = remaining % 60;
          setTimer(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          const overtime = elapsed - duration;
          const minutes = Math.floor(overtime / 60);
          const seconds = overtime % 60;
          setTimer(`+${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [courtData.timer.startTime, courtData.timer.duration, courtData.isActive, gameInfo]);

  // Handle 15-point radio button selection
  const handleTeam1QuickWin = () => {
    setTeam1Won15(true);
    setTeam2Won15(false);
    setTeam1Score('15');
    setTeam2Score(''); // Clear to let user enter losing score
  };

  const handleTeam2QuickWin = () => {
    setTeam2Won15(true);
    setTeam1Won15(false);
    setTeam2Score('15');
    setTeam1Score(''); // Clear to let user enter losing score
  };

  // Clear radio selections when manually editing scores
  const handleTeam1ScoreChange = (value) => {
    setTeam1Score(value);
    if (value !== '15') {
      setTeam1Won15(false);
    }
  };

  const handleTeam2ScoreChange = (value) => {
    setTeam2Score(value);
    if (value !== '15') {
      setTeam2Won15(false);
    }
  };

  // MLP Score validation
  const validateMLPScore = (score1, score2) => {
    // Must have valid scores
    if (!score1 || !score2) {
      return { valid: false, message: 'Please enter both team scores' };
    }

    const s1 = parseInt(score1);
    const s2 = parseInt(score2);

    // Basic validation
    if (isNaN(s1) || isNaN(s2) || s1 < 0 || s2 < 0) {
      return { valid: false, message: 'Please enter valid positive numbers' };
    }

    // Can't tie
    if (s1 === s2) {
      return { valid: false, message: 'Games cannot end in a tie' };
    }

    const winner = s1 > s2 ? s1 : s2;
    const loser = s1 > s2 ? s2 : s1;

    // Winner must score at least 15
    if (winner < 15) {
      return { valid: false, message: 'Winning team must score at least 15 points' };
    }

    // Must win by at least 2 (unless score is 15-13 or similar)
    if (winner >= 15 && loser >= 13 && (winner - loser) < 2) {
      return { valid: false, message: 'Must win by at least 2 points' };
    }

    // Loser can't score more than 15 unless it's a long game (like 17-15)
    if (loser > 15 && winner < 17) {
      return { valid: false, message: 'Invalid score combination' };
    }

    return { valid: true };
  };

  const handleSubmit = () => {
    const score1 = parseInt(team1Score) || 0;
    const score2 = parseInt(team2Score) || 0;
    
    // For MLP games, use enhanced validation
    if (gameInfo) {
      const validation = validateMLPScore(team1Score, team2Score);
      if (!validation.valid) {
        alert(validation.message);
        return;
      }
    } else {
      // SMART game validation (existing)
      if (score1 === 0 && score2 === 0) {
        alert('Please enter valid scores');
        return;
      }
    }
    
    onScoreSubmit(courtData.id, score1, score2);
    
    // Clear all scoring state
    setTeam1Score('');
    setTeam2Score('');
    setTeam1Won15(false);
    setTeam2Won15(false);
  };

  const getGameTypeDisplay = () => {
    if (!gameInfo) return null;
    
    const gameTypes = {
      'MM': "Men's Doubles",
      'FF': "Women's Doubles", 
      'MX1': 'Mixed Doubles (M1+F1)',
      'MX2': 'Mixed Doubles (M2+F2)'
    };
    return gameTypes[gameInfo.gameType] || gameInfo.gameTypeLabel || gameInfo.gameType;
  };

  const renderPlayer = (player, position) => {
    if (!player) {
      return (
        <div className={`player-position ${position}`}>
          <div className="player-name">Waiting...</div>
        </div>
      );
    }

    // For MLP games, show display names (already converted)
    if (gameInfo) {
      return (
        <div className={`player-position ${position}`}>
          <div className="player-name">{player}</div>
        </div>
      );
    }

    // For SMART games, show player object data
    return (
      <div className={`player-position ${position}`}>
        <div className="player-name">{player.name || player.displayName}</div>
        <div className="player-rating">({(player.currentRating || player.rating || 3.5).toFixed(1)})</div>
      </div>
    );
  };

  // UPDATED: Show team names and new format: "Tigers vs Dolphins: Mens Doubles"
  const renderGameInfo = () => {
    if (!gameInfo) return null;
    
    const gameTypeLabel = getGameTypeDisplay();
    let displayText = gameTypeLabel;
    
    // If we have team names available, use new format
    if (getTeamName && gameInfo.team1 && gameInfo.team2) {
      const team1Name = getTeamName(gameInfo.team1);
      const team2Name = getTeamName(gameInfo.team2);
      displayText = `${team1Name} vs ${team2Name}: ${gameTypeLabel}`;
    } else if (gameInfo.team1 && gameInfo.team2) {
      // Fallback to team IDs if no getTeamName function
      displayText = `Team ${gameInfo.team1} vs Team ${gameInfo.team2}: ${gameTypeLabel}`;
    }
    
    return (
      <div style={{
        textAlign: 'center',
        marginBottom: '8px',
        color: '#deff01',
        fontWeight: 'bold',
        fontSize: '14px'
      }}>
        {displayText}
      </div>
    );
  };

  if (!courtData.isActive && !gameInfo) {
    return (
      <div className="court-container">
        <div className="court-header">
          <h3 className="court-title">Court {courtData.id}</h3>
        </div>
        <div className="court-post top-post"></div>
        <div className="court-waiting">
          <div>Waiting for players...</div>
        </div>
        <div className="court-post bottom-post"></div>
      </div>
    );
  }

  const players = gameInfo ? 
    [...(gameInfo.players.team1 || []), ...(gameInfo.players.team2 || [])] :
    courtData.players || [];

  return (
    <div className="court-container">
      <div className="court-header">
        <h3 className="court-title">Court {courtData.id}</h3>
        <div className="court-controls">
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={!team1Score && !team2Score}
          >
            Submit Score
          </button>
          
          {/* Only show timer for SMART sessions */}
          {!gameInfo && (
            <div className="timer-container">
              <div className={`timer ${timer.includes('+') ? 'overtime' : 'regular'}`}>
                {timer}
              </div>
            </div>
          )}
        </div>
      </div>

      {renderGameInfo()}
      
      <div className="court-post top-post"></div>
      
      <div className="court">
        <div className="team-section left-team">
          {renderPlayer(players[0], 'top-left')}
          {renderPlayer(players[1], 'bottom-left')}
        </div>
        
        <div className="score-section">
          {/* MLP Quick Win Radio Button */}
          {gameInfo && (
            <div className="quick-win-section">
              <label className="quick-win-radio">
                <input
                  type="radio"
                  checked={team1Won15}
                  onChange={handleTeam1QuickWin}
                />
                <span className="radio-label">15</span>
              </label>
            </div>
          )}
          
          <input
            type="number"
            min="0"
            max="25"
            value={team1Score}
            onChange={(e) => handleTeam1ScoreChange(e.target.value)}
            className="score-input"
            placeholder="0"
          />
        </div>
        
        <div className="net"></div>
        
        <div className="score-section">
          {/* MLP Quick Win Radio Button */}
          {gameInfo && (
            <div className="quick-win-section">
              <label className="quick-win-radio">
                <input
                  type="radio"
                  checked={team2Won15}
                  onChange={handleTeam2QuickWin}
                />
                <span className="radio-label">15</span>
              </label>
            </div>
          )}
          
          <input
            type="number"
            min="0"
            max="25"
            value={team2Score}
            onChange={(e) => handleTeam2ScoreChange(e.target.value)}
            className="score-input"
            placeholder="0"
          />
        </div>
        
        <div className="team-section right-team">
          {renderPlayer(players[2], 'top-right')}
          {renderPlayer(players[3], 'bottom-right')}
        </div>
      </div>
      
      <div className="court-post bottom-post"></div>
    </div>
  );
};

export default Court;